import { useAuthStore } from 'lib/stores/authStore';
import type { EventContractWsChannel, WsSubscriberCallback } from 'models/ws';
import { WS_CLOSE_CODE_INACTIVITY_RECYCLE } from 'transport/ws';
import { eventContractWsClient } from 'transport/wsClients';

import { createNextTaskScheduler } from './createNextTaskScheduler';

export interface WatcherChannelConfig {
    channel: EventContractWsChannel;
    handler: WsSubscriberCallback;
    sendInitialSnapshot?: boolean;
    /**
     * Set for channels the gateway treats as a global broadcast — `market_tickers`
     * is silently ignored and `update_subscription` is a no-op ack (e.g.
     * `market_lifecycle_v2`). Such channels subscribe once with no filter and stay
     * subscribed until refCounts drain to zero. Reducers fed by these channels
     * should call `isWatched` to drop messages for tickers no caller cares about.
     */
    globalBroadcast?: boolean;
}

export interface TickerWatcherOptions {
    channels: WatcherChannelConfig[];
    onAllUnwatched?: () => void;
    /**
     * Fired when one or more tickers transition from refCount > 0 to 0. Lets the
     * caller drop accumulated state (e.g. cached quotes/statuses) for tickers
     * nobody is reading anymore — important under unbounded ticker churn (user
     * navigating cards) where the store would otherwise leak entries forever.
     */
    onTickersDropped?: (tickers: string[]) => void;
    /**
     * Fired when the transport reconnects and `ws.resubscribe` re-establishes
     * this watcher's subscriptions — which re-delivers one fresh snapshot per
     * watched ticker. Receives the currently-watched tickers so the caller can
     * invalidate per-ticker state that only a fresh snapshot may re-establish
     * (e.g. drop a stale "snapshot confirmed" flag, re-arm snapshot recovery).
     */
    onReconnect?: (tickers: string[]) => void;
    /**
     * Fired when the transport drops after having been connected, while tickers
     * are watched. Live data stops flowing until reconnect, so callers should
     * invalidate per-ticker state that consumers treat as "currently live" —
     * e.g. cached quotes that both shadow fresher REST data and gate REST-polling
     * fallbacks — so those fallbacks can re-arm for the duration of the outage.
     */
    onDisconnect?: (tickers: string[]) => void;
}

export interface TickerWatcherActions {
    watch: (ticker: string) => void;
    unwatch: (ticker: string) => void;
    isWatched: (ticker: string) => boolean;
    /**
     * Refresh a snapshot-bearing ticker using only gateway-supported membership
     * updates. If the ticker is already present, remove it first, then add it
     * back with initial snapshot delivery enabled for channels that requested
     * snapshots on subscribe. Returns `true` if at least one add request was
     * sent. The watcher owns the subscription, so callers don't re-derive the sid.
     */
    refreshTickerSnapshot: (ticker: string) => boolean;
}

export function createTickerWatcher(options: TickerWatcherOptions): TickerWatcherActions {
    const { channels, onAllUnwatched, onTickersDropped, onReconnect, onDisconnect } = options;

    const refCounts = new Map<string, number>();
    // Tracks unsubscribe closures we got back from the WS client so we can tear
    // down channels when refCounts hit zero.
    const cleanups = new Map<EventContractWsChannel, () => void>();

    const buffer = new Map<string, number>();
    let flushScheduled = false;

    function syncChannel(config: WatcherChannelConfig, activeTickers: string[]): boolean {
        const { channel, handler, sendInitialSnapshot, globalBroadcast } = config;

        // The exchange WS gateway rejects the handshake outright for anonymous
        // visitors (no valid session/CSRF), so don't even attempt it — `watch()`
        // still records desired tickers via `refCounts` for when a REST-polling
        // fallback is available; `ensureAuthStateListener` below replays this sync
        // once the visitor logs in.
        if (!useAuthStore.getState().isLoggedIn) {
            return true;
        }

        // We call `findSubscriptionBySubscriber` before any subscribe, so the WS
        // service may not be lazy-init'd yet. In practice
        // another consumer (auth/portfolio/etc.) has usually init'd it first; this is
        // belt-and-suspenders for the case where this watcher is the first WS user.
        eventContractWsClient.connect();
        const found = eventContractWsClient.findSubscriptionBySubscriber(handler);

        if (found?.state === 'pending') {
            // Subscribe is in flight; wait for the `subscribed` ack rather than firing
            // a duplicate subscribe (which trips server `code:6 Already subscribed`).
            return false;
        }

        if (found?.state === 'deferred') {
            // The server rejected this command. Preserve the desired subscription,
            // but do not poll until the transport replays it on reconnect.
            return true;
        }

        if (globalBroadcast) {
            // Already subscribed → nothing to reconcile; the gateway streams every
            // event regardless of which tickers we currently care about.
            if (found) return true;

            const cleanup = eventContractWsClient.subscribe({ channels: [channel] }, handler);
            cleanups.set(channel, cleanup);
            return true;
        }

        if (found) {
            // Confirmed subscription — reconcile ticker membership via `update_subscription`.
            const currentTickers = new Set(found.params.market_tickers ?? []);
            const nextTickers = new Set(activeTickers);
            const toAdd = activeTickers.filter((t) => !currentTickers.has(t));
            const toRemove = [...currentTickers].filter((t) => !nextTickers.has(t));

            if (toAdd.length > 0) {
                eventContractWsClient.updateSubscription(
                    found.sid,
                    'add_markets',
                    toAdd,
                    sendInitialSnapshot ? { sendInitialSnapshot: true } : undefined,
                );
            }
            if (toRemove.length > 0) {
                eventContractWsClient.updateSubscription(found.sid, 'delete_markets', toRemove);
            }
            return true;
        }

        const cleanup = eventContractWsClient.subscribe(
            {
                channels: [channel],
                market_tickers: activeTickers,
                ...(sendInitialSnapshot && { send_initial_snapshot: true }),
            },
            handler,
        );
        cleanups.set(channel, cleanup);
        return true;
    }

    function syncSubscription(): void {
        const activeTickers = [...refCounts.keys()];

        if (activeTickers.length === 0) {
            for (const cleanup of cleanups.values()) cleanup();
            cleanups.clear();
            onAllUnwatched?.();
            return;
        }

        for (const config of channels) {
            if (!syncChannel(config, activeTickers)) {
                scheduleSync();
                return;
            }
        }
    }

    const syncScheduler = createNextTaskScheduler(syncSubscription);

    function scheduleSync(): void {
        syncScheduler.schedule();
    }

    // Deferred until the first `watch()` call rather than registered eagerly here —
    // some callers (e.g. `marketEntitiesStore`) are imported by code that never ends
    // up watching anything (analytics helpers, type-only consumers), and eagerly
    // touching `eventContractWsClient` at module-import time forces every transitive
    // importer to mock `transport/wsClients`, even when this watcher never fires a
    // single subscription. Safe to defer: the listener below is a no-op
    // (`refCounts.size === 0`) until something calls `watch()` anyway, so registering
    // it lazily changes no observable behavior.
    //
    // No cleanup once registered: this watcher is created once at module-init time by
    // the singleton `tickerQuotesStore`, so a leaked listener would only matter under
    // HMR module re-evaluation in dev — at which point the whole store is rebuilt anyway.
    let connectionStateListenerRegistered = false;
    function ensureConnectionStateListener(): void {
        if (connectionStateListenerRegistered) return;
        connectionStateListenerRegistered = true;

        // Tracked so `onDisconnect` fires only on a connected → disconnected TRANSITION — the
        // client may report a disconnected state repeatedly while retrying, and callers use this
        // signal to invalidate caches exactly once per outage.
        let wasConnected = false;
        eventContractWsClient.subscribeConnectionState((state) => {
            const previouslyConnected = wasConnected;
            wasConnected = state.connected;
            if (refCounts.size === 0) return;
            scheduleSync();
            // `connected` only fires on socket open, so every occurrence re-runs
            // `ws.resubscribe` and re-delivers a fresh snapshot per ticker. Notify
            // the caller so it can invalidate state the fresh snapshot supersedes.
            // (The very first connect is harmless: nothing is confirmed yet, and
            // recovery was already armed by `watch`.)
            if (state.connected) {
                onReconnect?.([...refCounts.keys()]);
            } else if (previouslyConnected && state.code !== WS_CLOSE_CODE_INACTIVITY_RECYCLE) {
                // Skip the client's own inactivity recycle: zero messages arrived on the old
                // socket and the reconnect is immediate, so cached state can't have gone stale —
                // the same carve-out `ws.ts` documents for connection-state resyncs.
                onDisconnect?.([...refCounts.keys()]);
            }
        });
    }

    // Mirrors `ensureConnectionStateListener` above: deferred until the first `watch()`,
    // no cleanup once registered (singleton watcher, lives for the app's lifetime).
    // Replays the sync pass once a previously-anonymous visitor logs in, since
    // `syncChannel` no-ops on every pass while logged out (the gateway rejects
    // unauthenticated handshakes) and nothing else would retry already-watched tickers.
    let authStateListenerRegistered = false;
    function ensureAuthStateListener(): void {
        if (authStateListenerRegistered) return;
        authStateListenerRegistered = true;

        useAuthStore.subscribe((state, prevState) => {
            if (refCounts.size === 0) return;
            if (state.isLoggedIn && !prevState.isLoggedIn) scheduleSync();
        });
    }

    function flushBuffer(): void {
        flushScheduled = false;
        if (buffer.size === 0) return;

        let activeSetChanged = false;
        const dropped: string[] = [];

        for (const [ticker, delta] of buffer) {
            const prev = refCounts.get(ticker) ?? 0;
            const next = Math.max(0, prev + delta);

            if (next === 0 && prev > 0) {
                refCounts.delete(ticker);
                dropped.push(ticker);
                activeSetChanged = true;
            } else if (next > 0 && prev === 0) {
                refCounts.set(ticker, next);
                activeSetChanged = true;
            } else if (next > 0) {
                refCounts.set(ticker, next);
            }
        }

        buffer.clear();

        if (dropped.length > 0) {
            onTickersDropped?.(dropped);
        }

        if (activeSetChanged) {
            scheduleSync();
        }
    }

    function scheduleFlush(): void {
        if (flushScheduled) return;
        flushScheduled = true;
        queueMicrotask(flushBuffer);
    }

    return {
        watch(ticker: string): void {
            ensureConnectionStateListener();
            ensureAuthStateListener();
            buffer.set(ticker, (buffer.get(ticker) ?? 0) + 1);
            scheduleFlush();
        },
        unwatch(ticker: string): void {
            buffer.set(ticker, (buffer.get(ticker) ?? 0) - 1);
            scheduleFlush();
        },
        isWatched(ticker: string): boolean {
            return refCounts.has(ticker);
        },
        refreshTickerSnapshot(ticker: string): boolean {
            // Reuse the per-channel handler the watcher subscribed with so the sid lookup stays
            // here rather than leaking to callers. If the client was never lazily initialized,
            // `findSubscriptionBySubscriber` reports no confirmed subscription and every channel
            // is skipped below — correct, since there's nothing to refresh in that case anyway.
            let refreshed = false;
            for (const { handler, globalBroadcast, sendInitialSnapshot } of channels) {
                if (globalBroadcast) continue;
                const found = eventContractWsClient.findSubscriptionBySubscriber(handler);
                if (found?.state !== 'confirmed') continue;

                const currentTickers = found.params.market_tickers ?? [];
                if (
                    currentTickers.includes(ticker) &&
                    !eventContractWsClient.updateSubscription(found.sid, 'delete_markets', [ticker])
                ) {
                    continue;
                }

                if (
                    eventContractWsClient.updateSubscription(
                        found.sid,
                        'add_markets',
                        [ticker],
                        sendInitialSnapshot ? { sendInitialSnapshot: true } : undefined,
                    )
                ) {
                    refreshed = true;
                } else {
                    scheduleSync();
                }
            }
            return refreshed;
        },
    };
}
